import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {CopilotAppOpenAction, CopilotAppOpenActionMenuItem} from '@github-ui/copilot-app-launcher-shared'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {McpInstallButton, type McpInstallTarget} from '@github-ui/mcp-install-button'
import {TerminalIcon, TriangleDownIcon, type Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, Button, Link} from '@primer/react'
import {useId, useState} from 'react'

import styles from './AIResourceResults.module.css'

export type AIResourceInstallAction =
  | {type: 'mcp_server'; target: McpInstallTarget}
  | {type: 'skill'; command: string; hasCompanionFiles: boolean}

export type AIResourceResult = {
  id: string
  /** An authorized canonical destination supplied by the composing application. */
  href: string
  name: string
  description?: string
  resourceType: string
  resourceTypeIcon?: Icon
  publisher?: {
    name: string
    /** An authorized canonical destination supplied by the composing application. */
    href: string
  }
  labels: readonly string[]
  installAction?: AIResourceInstallAction
  connectorIds?: readonly string[]
}

export type AIResourceResultsProps = {
  results: readonly AIResourceResult[]
}

export function AIResourceResults({results}: AIResourceResultsProps) {
  if (results.length === 0) return null

  return (
    <ListView itemsListClassName={styles.List} title="AI resources" totalCount={results.length}>
      {results.map(result => (
        <AIResourceResultRow key={result.id} result={result} />
      ))}
    </ListView>
  )
}

export function AIResourceResultRow({result}: {result: AIResourceResult}) {
  const ResourceTypeIcon = result.resourceTypeIcon
  const canViewConnector =
    isFeatureEnabled('agentfinder_connectors_ghapp_deeplink') && (result.connectorIds?.length ?? 0) > 0

  return (
    <ListItem
      className={styles.Row}
      title={<ListItemTitle href={result.href} value={result.name} linkProps={{as: Link, className: styles.Link}} />}
      metadata={
        result.installAction || canViewConnector ? (
          <InstallAction action={result.installAction} name={result.name} canViewConnector={canViewConnector} />
        ) : undefined
      }
      metadataContainerClassName={styles.InstallMetadata}
    >
      <ListItemMainContent>
        <div className={styles.Content}>
          {result.description && (
            <ListItemDescription>
              <ListItemDescriptionItem>{result.description}</ListItemDescriptionItem>
            </ListItemDescription>
          )}

          <ListItemDescription>
            <span className={styles.Metadata}>
              <span className={styles.ResourceType}>
                {ResourceTypeIcon && <ResourceTypeIcon aria-hidden="true" data-testid="resource-type-icon" />}
                <span>{result.resourceType}</span>
              </span>
              {result.publisher && (
                <>
                  <span className={styles.Separator} aria-hidden="true" />
                  <Link className={styles.Link} href={result.publisher.href}>
                    {result.publisher.name}
                  </Link>
                </>
              )}
            </span>
          </ListItemDescription>
        </div>
      </ListItemMainContent>
    </ListItem>
  )
}

function GitHubAppConnectorAction({inMenu = true}: {inMenu?: boolean}) {
  const AppAction = inMenu ? CopilotAppOpenActionMenuItem : CopilotAppOpenAction
  return (
    <AppAction
      open="ghapp://extensions/connectors"
      entryPoint="dotcom_ai-resource-search_connectors"
      // eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- GitHub App is the product name.
      label="View in GitHub App"
    />
  )
}

function InstallAction({
  action,
  name,
  canViewConnector,
}: {
  action?: AIResourceInstallAction
  name: string
  canViewConnector: boolean
}) {
  if (action?.type === 'mcp_server') {
    return (
      <McpInstallButton
        mcp={action.target}
        muted
        ariaLabel={`Install ${name}`}
        analyticsResourceName={name}
        copilotAppEntryPoint="dotcom_ai-resource-search_install"
      >
        {canViewConnector && <GitHubAppConnectorAction />}
      </McpInstallButton>
    )
  }

  if (action?.type === 'skill') {
    return <SkillInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  return (
    <ActionMenu>
      <ActionMenu.Button size="small" aria-label={`Install ${name}`}>
        Install
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <GitHubAppConnectorAction />
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function SkillInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Extract<AIResourceInstallAction, {type: 'skill'}>
  name: string
  canViewConnector: boolean
}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      overlayProps={{
        'aria-labelledby': headingId,
        'aria-modal': true,
        role: 'dialog',
      }}
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          aria-haspopup="dialog"
          aria-label={`Install ${name}`}
          size="small"
          trailingVisual={TriangleDownIcon}
        >
          Install
        </Button>
      )}
      width="large"
    >
      <div className={styles.SkillInstallMenu}>
        <h4 className={styles.SkillInstallHeading} id={headingId}>
          <TerminalIcon aria-hidden="true" data-testid="skill-install-terminal-icon" />
          <span>{canViewConnector ? 'Install options' : 'Copilot CLI'}</span>
        </h4>
        <SkillInstallOption
          title={action.hasCompanionFiles ? 'Install skill with supporting files' : 'Install skill'}
          description={
            action.hasCompanionFiles
              ? 'Clone the repository to include the supporting files this skill requires. Keep the clone in place.'
              : 'This skill doesn’t require any supporting files.'
          }
          command={action.command}
          multiline={action.hasCompanionFiles}
        />
        {canViewConnector && (
          <ActionList>
            <GitHubAppConnectorAction inMenu={false} />
          </ActionList>
        )}
      </div>
    </AnchoredOverlay>
  )
}

function SkillInstallOption({
  title,
  description,
  command,
  multiline = false,
}: {
  title: string
  description: string
  command: string
  multiline?: boolean
}) {
  return (
    <section className={styles.SkillInstallOption}>
      <h5 className={styles.SkillInstallOptionHeading}>{title}</h5>
      <p className={styles.SkillInstallDescription}>{description}</p>
      <div className={styles.SkillInstallCommand}>
        <pre
          aria-label={`${title} command`}
          className={`${styles.SkillInstallCode} ${multiline ? styles.SkillInstallMultilineCode : ''}`}
          role="region"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard-scrollable overflow region
          tabIndex={0}
        >
          <code>{command}</code>
        </pre>
        <CopyToClipboardIconButton
          className={styles.SkillInstallCopyButton}
          textToCopy={command}
          ariaLabel="Copy command to clipboard"
          tooltipProps={{direction: 'nw'}}
        />
      </div>
    </section>
  )
}
